"use client";

import './index.css';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useId, useEffect, createElement, cloneElement } from 'react';
import { validateSurface } from '../../globals.js';
import { Scrollable } from '../../scrollable/scrollable/index.js';
import getOS from '../../utilities/check-os/index.js';
import checkIfMobileDevice from '../../utilities/check-device/index.js';
import calculateRem from '../../utilities/calculate-rem/index.js';
import '../../_virtual/_tslib.js';
import useViewport from '../../utilities/use-viewport/index.js';
import TabItem from './tab-item.js';
import styles from './tabs.module.scss.js';

const scrollableDefaultValue = {
    minimizeHitArea: false,
    overflowOrientation: 'vertical',
    verticalScrollbarInset: {
        top: 'var(--vds-space-1x)',
        right: 'var(--vds-space-1x)',
        bottom: 'var(--vds-space-1x)',
        left: '0px',
    },
    scrollContainerInset: {
        top: '0px',
        right: 'var(--vds-space-4x)',
        left: '0px',
        bottom: '0px',
    },
    contentMargin: {
        top: 'var(--vds-space-4x)',
        right: '0px',
        left: '0px',
        bottom: 'var(--vds-space-4x)',
    },
};
const _formatCssValue = (value) => {
    if (!Number.isNaN(Number(value))) {
        return `${Number(value)}px`;
    }
    return value;
};
/**
 * @component Tabs
 * @package Tabs
 * @document ../_docs/code/Tabs.editor.tsx
 * @document ../_docs/code/Tabs.snippet.tsx
 * @document ../_docs/code/Tabs.import.tsx
 */
const Tabs = ({ children, borderLine = 'inline', selectedIndex: defaultSelectedIndex = 0, minWidth = calculateRem('44px'), width, size = 'medium', orientation: orientationProp = 'horizontal', overflow: overflowProp = 'scroll', uniformWidth, surface: surfaceProp = 'lightPrimary', animated = 'none', id, viewport, sticky, onTabChange, highlight = true, height, scrollable, alignment, items, behavior = 'tab', }) => {
    const _removeHiddenTabs = (unFilteredChildren) => unFilteredChildren.filter((child) => !child.props.hide);
    const filteredItems = items === null || items === void 0 ? void 0 : items.filter((item) => !item.hide);
    const isItemsMode = Array.isArray(filteredItems) && filteredItems.length > 0;
    const filteredChildren = isItemsMode ? [] : _removeHiddenTabs(children);
    const surface = validateSurface(surfaceProp);
    const resolveSelectedIndex = () => {
        if (isItemsMode) {
            const lastSelectedIndex = filteredItems.reduce((accumulator, item, idx) => (item.selected ? idx : accumulator), -1);
            return lastSelectedIndex >= 0 ? lastSelectedIndex : defaultSelectedIndex;
        }
        const lastSelectedIndex = filteredChildren.findLastIndex((child) => child.props.selected);
        return lastSelectedIndex >= 0 ? lastSelectedIndex : defaultSelectedIndex;
    };
    const _calculateScrollable = () => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        if (!scrollable || Object.keys(scrollable).length === 0) {
            return scrollableDefaultValue;
        }
        return {
            minimizeHitArea: false,
            overflowOrientation: scrollable.overflowOrientation || 'vertical',
            verticalScrollbarInset: {
                top: ((_a = scrollable.verticalScrollbarInset) === null || _a === void 0 ? void 0 : _a.top) || `var(--vds-space-1x)`,
                right: ((_b = scrollable.verticalScrollbarInset) === null || _b === void 0 ? void 0 : _b.right) || `var(--vds-space-1x)`,
                bottom: ((_c = scrollable.verticalScrollbarInset) === null || _c === void 0 ? void 0 : _c.bottom) || `var(--vds-space-1x)`,
                left: ((_d = scrollable.verticalScrollbarInset) === null || _d === void 0 ? void 0 : _d.left) || '0px',
            },
            scrollContainerInset: {
                top: ((_e = scrollable.scrollContainerInset) === null || _e === void 0 ? void 0 : _e.top) || '0px',
                right: ((_f = scrollable.scrollContainerInset) === null || _f === void 0 ? void 0 : _f.right) || `var(--vds-space-4x)`,
                bottom: ((_g = scrollable.scrollContainerInset) === null || _g === void 0 ? void 0 : _g.bottom) || '0px',
                left: ((_h = scrollable.scrollContainerInset) === null || _h === void 0 ? void 0 : _h.left) || '0px',
            },
            contentMargin: {
                top: ((_j = scrollable.contentMargin) === null || _j === void 0 ? void 0 : _j.top) || `var(--vds-space-4x)`,
                right: ((_k = scrollable.contentMargin) === null || _k === void 0 ? void 0 : _k.right) || '0px',
                bottom: ((_l = scrollable.contentMargin) === null || _l === void 0 ? void 0 : _l.bottom) || `var(--vds-space-4x)`,
                left: ((_m = scrollable.contentMargin) === null || _m === void 0 ? void 0 : _m.left) || '0px',
            },
        };
    };
    /**
     * refs
     */
    const tabRefs = useRef([]);
    const tabsContainerRef = useRef(null);
    const tabPanelRef = useRef(null);
    const tabInfoRef = useRef([]);
    const selectionInitiatedByKeyboard = useRef(false);
    /**
     * states and consts
     */
    const [selectedIndex, setSelectedIndex] = useState(resolveSelectedIndex());
    const [scrollableWrapperHeight, setScrollableWrapperHeight] = useState(height);
    const [borderLinePosition, setBorderLinePosition] = useState('0px');
    const contentFocusTimeout = useRef();
    const _useId = useId();
    const tabsId = useRef();
    if (tabsId.current === undefined) {
        tabsId.current = id || _useId;
    }
    const overflow = checkIfMobileDevice() ? 'scroll' : overflowProp;
    const viewportHookValue = useViewport();
    const isMobile = viewport === 'mobile' || viewportHookValue === 'mobile';
    const orientation = isMobile ? 'horizontal' : orientationProp;
    const isAndroid = getOS() === 'android';
    const isIos = getOS() === 'ios';
    /**
     * styles
     */
    const tabsStyles = [
        styles.tabs,
        styles[orientation],
        styles[size],
        styles[surface.toLowerCase()],
    ];
    if (alignment === 'center') {
        tabsStyles.push(styles.centerAlign);
    }
    if (sticky && height) {
        tabsStyles.push(styles.stickyFixedHeight);
    }
    if (sticky) {
        tabsStyles.push(styles.sticky);
    }
    if (borderLine !== 'none') {
        tabsStyles.push(orientation === 'vertical' ? styles.borderLineVertical : styles.borderLine);
        tabsStyles.push(styles[borderLine]);
    }
    if (overflow === 'scroll') {
        tabsStyles.push(styles.overflowScroll);
    }
    const tabsContainerWrapperStyles = [styles.tabsContainerWrapper];
    const tabsContainerStyles = [styles.tabsContainer];
    const contentWrapperStyles = [styles.contentWrapper];
    useEffect(() => {
        if (contentFocusTimeout.current)
            clearTimeout(contentFocusTimeout.current);
    }, []);
    useEffect(() => {
        if (defaultSelectedIndex !== undefined &&
            defaultSelectedIndex !== selectedIndex) {
            setSelectedIndex(resolveSelectedIndex());
        }
    }, [defaultSelectedIndex]);
    useEffect(() => {
        if (selectionInitiatedByKeyboard.current) {
            // We use a short timeout to ensure the browser has fully processed
            // the DOM update before we shift focus. This resolves the race condition.
            const focusTimeout = setTimeout(() => {
                var _a;
                (_a = tabPanelRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 50); // A small 50ms delay.
            // Reset the flag immediately.
            selectionInitiatedByKeyboard.current = false;
            // This is a cleanup function. It prevents issues if the user
            // changes tabs again before the timeout has finished.
            return () => clearTimeout(focusTimeout);
        }
    }, [selectedIndex]);
    useEffect(() => {
        if (borderLine !== 'fullBleed' || !tabsContainerRef.current)
            return;
        const calculateBorderLinePosition = () => {
            if (tabsContainerRef.current) {
                const tabsRect = tabsContainerRef.current.getBoundingClientRect();
                const viewportPosition = orientation === 'vertical'
                    ? tabsRect.left + window.scrollX
                    : tabsRect.bottom + window.scrollY - 1;
                let parent = tabsContainerRef.current.parentElement;
                let positionedParentViewportPosition = 0;
                while (parent) {
                    const parentStyle = window.getComputedStyle(parent);
                    if (parentStyle.position !== 'static') {
                        const parentRect = parent.getBoundingClientRect();
                        positionedParentViewportPosition =
                            orientation === 'vertical'
                                ? parentRect.left + window.scrollX
                                : parentRect.top + window.scrollY;
                        break;
                    }
                    parent = parent.parentElement;
                }
                const relativePosition = viewportPosition - positionedParentViewportPosition;
                setBorderLinePosition(_formatCssValue(relativePosition || '0px'));
            }
        };
        const updatePosition = () => {
            calculateBorderLinePosition();
        };
        calculateBorderLinePosition();
        window.addEventListener('scroll', updatePosition);
        const observer = new ResizeObserver(updatePosition);
        observer.observe(tabsContainerRef.current);
        return () => {
            window.removeEventListener('scroll', updatePosition);
            observer.disconnect();
        };
    }, [borderLine, size, sticky, orientation]);
    useEffect(() => {
        if (orientationProp === 'vertical' && tabsContainerRef.current) {
            const scrollableValues = _calculateScrollable();
            const tabsHeight = _formatCssValue(tabsContainerRef.current.clientHeight);
            const marginTop = _formatCssValue(scrollableValues.contentMargin.top);
            const marginBottom = _formatCssValue(scrollableValues.contentMargin.bottom);
            const insetTop = _formatCssValue(scrollableValues.scrollContainerInset.top);
            const insetBottom = _formatCssValue(scrollableValues.scrollContainerInset.bottom);
            const heightProp = _formatCssValue(height);
            setScrollableWrapperHeight(height
                ? `calc(${heightProp} - ${marginTop} - ${marginBottom} - ${insetTop} - ${insetBottom})`
                : tabsHeight);
        }
    }, [orientationProp, height, scrollable, size, viewportHookValue]);
    const _onClickTab = (e, newSelectedIndex, childOnClick) => {
        contentFocusTimeout.current = setTimeout(() => {
            tabPanelRef.current.focus({ preventScroll: true }); // to prevent scroll when triggered by mouse
        }, 200);
        setSelectedIndex(newSelectedIndex);
        if (onTabChange)
            onTabChange(e, newSelectedIndex);
        if (childOnClick)
            childOnClick(e, newSelectedIndex);
        /* istanbul ignore else -- @preserve */
        if (tabsId === null || tabsId === void 0 ? void 0 : tabsId.current) {
            const element = document.getElementById(tabsId === null || tabsId === void 0 ? void 0 : tabsId.current);
            /* istanbul ignore else -- @preserve */
            if ((element === null || element === void 0 ? void 0 : element.getBoundingClientRect().top) <= 0)
                element.scrollIntoView({ block: 'start' });
        }
    };
    const _onKeyDownTab = (e, index) => {
        const lastIndex = isItemsMode
            ? filteredItems.length - 1
            : filteredChildren.length - 1;
        if (e.key === 'Enter' || e.key === 'space') {
            selectionInitiatedByKeyboard.current = true;
            setSelectedIndex(index);
            if (onTabChange)
                onTabChange(e, index);
        }
        if ((e.key === 'ArrowLeft' && orientation === 'horizontal') ||
            (e.key === 'ArrowUp' &&
                orientation === 'vertical' &&
                viewport !== 'mobile')) {
            if (index === 0) {
                tabRefs.current[lastIndex].focus();
            }
            else {
                tabRefs.current[index - 1].focus();
            }
        }
        if ((e.key === 'ArrowRight' && orientation === 'horizontal') ||
            (e.key === 'ArrowDown' &&
                orientation === 'vertical' &&
                viewport !== 'mobile')) {
            if (index === lastIndex) {
                tabRefs.current[0].focus();
            }
            else {
                tabRefs.current[index + 1].focus();
            }
        }
        if (e.key === 'End') {
            e.preventDefault();
            tabRefs.current[lastIndex].focus();
        }
        if (e.key === 'Home') {
            e.preventDefault();
            tabRefs.current[0].focus();
        }
    };
    const _renderTabs = () => isItemsMode
        ? filteredItems.map((tabItem, index) => {
            const uniqueId = (tabItem === null || tabItem === void 0 ? void 0 : tabItem.uniqueId) || `${tabsId === null || tabsId === void 0 ? void 0 : tabsId.current}${index}`;
            tabInfoRef.current[index] = {
                uniqueId,
                label: tabItem.label,
                tabPanelAriaLabel: tabItem.tabPanelAriaLabel,
            };
            return (createElement(TabItem, Object.assign({}, tabItem, { key: index + 1, index: index, selected: index === selectedIndex, onClick: (e) => {
                    _onClickTab(e, index, tabItem.onClick);
                }, setRef: (element) => {
                    tabRefs.current[index] = element;
                }, viewport: viewport, uniqueId: uniqueId, orientation: orientation, uniformWidth: uniformWidth && orientation === 'horizontal', size: size, surface: surface, minWidth: (tabItem.minWidth || minWidth), overflow: overflow, highlight: highlight, onKeyDown: (e) => {
                    _onKeyDownTab(e, index);
                }, behavior: behavior })));
        })
        : filteredChildren.map((child, index) => {
            const { 'data-analyticstrack': analyticsTrack, 'data-track': track, 'data-track-ignore': ignoreTrack, 'data-clickstream': clickStream, onClick, tabPanelAriaLabel, href, target, label, } = child.props;
            const uniqueId = child.props.uniqueId || `${tabsId === null || tabsId === void 0 ? void 0 : tabsId.current}${index}`;
            tabInfoRef.current[index] = {
                uniqueId,
                tabPanelAriaLabel,
                label,
            };
            return cloneElement(child, {
                label,
                viewport,
                key: index + 1,
                orientation,
                index,
                selected: index === selectedIndex,
                onClick: (e) => {
                    _onClickTab(e, index, onClick);
                },
                setRef(element) {
                    tabRefs.current[index] = element;
                },
                hide: child.props.hide,
                minWidth: child.props.minWidth || minWidth,
                width: child.props.width,
                uniformWidth: uniformWidth && orientation === 'horizontal',
                size,
                uniqueId,
                analyticsTrack,
                track,
                ignoreTrack,
                clickStream,
                surface,
                overflow,
                highlight,
                href,
                target,
                behavior,
                onKeyDown: (e) => _onKeyDownTab(e, index),
            });
        });
    const _renderContent = () => {
        const contentChild = isItemsMode
            ? filteredItems[selectedIndex]
            : filteredChildren[selectedIndex];
        const canRenderScreenReaderLabel = !isAndroid &&
            tabInfoRef.current[selectedIndex] &&
            tabInfoRef.current[selectedIndex].tabPanelAriaLabel
            ? true
            : !!isIos;
        return (jsxs("div", { className: styles.childWrapper, tabIndex: isAndroid ? 0 : undefined, children: [canRenderScreenReaderLabel && (jsx("div", { className: styles.screenReaderText, tabIndex: tabInfoRef.current[selectedIndex].tabPanelAriaLabel
                        ? 0
                        : undefined, children: `${tabInfoRef.current[selectedIndex].label} tab selected ${selectedIndex + 1} of ${filteredItems ? filteredItems.length : filteredChildren.length} ${tabInfoRef.current[selectedIndex].tabPanelAriaLabel || ''} ` })), jsx("div", { className: animated !== 'none' ? styles[animated.toLowerCase()] : undefined, children: isItemsMode ? contentChild.children : contentChild.props.children }, selectedIndex)] }));
    };
    const selectedLabel = selectedIndex !== undefined && tabInfoRef.current[selectedIndex]
        ? `${tabInfoRef.current[selectedIndex].label} tab selected ${selectedIndex + 1} of ${filteredChildren.length}`
        : '';
    const getScrollableWrapperHeight = () => {
        if (!tabsContainerRef.current)
            return '0px';
        return _formatCssValue(scrollableWrapperHeight || '');
    };
    const scrollableProps = _calculateScrollable();
    const getTablistStyles = () => (Object.assign({}, (borderLine === 'fullBleed'
        ? { '--border-line-pos': borderLinePosition }
        : {})));
    return (jsxs("div", { id: tabsId.current, className: tabsStyles.join(' '), style: Object.assign({ '--tabs-height': _formatCssValue(height || '100%'), '--tabs-width': _formatCssValue(width || '100%') }, (borderLine === 'inline' &&
            orientation === 'vertical' && {
            '--scrollable-top-margin': scrollableProps.contentMargin.top,
            '--scrollable-bottom-margin': scrollableProps.contentMargin.bottom,
        })), children: [jsx("div", { className: tabsContainerWrapperStyles.join(' '), children: orientation === 'vertical' ? (jsx(Scrollable, Object.assign({}, scrollableProps, { containerHeight: getScrollableWrapperHeight(), surface: surface, children: jsx("ul", { style: getTablistStyles(), role: behavior === 'tab' ? 'tablist' : 'navigation', ref: tabsContainerRef, className: tabsContainerStyles.join(' '), children: _renderTabs() }) }))) : (jsx("ul", { style: getTablistStyles(), role: behavior === 'tab' ? 'tablist' : 'navigation', ref: tabsContainerRef, className: tabsContainerStyles.join(' '), children: _renderTabs() })) }), behavior !== 'navigation' && (jsx("div", { className: contentWrapperStyles.join(' '), ref: tabPanelRef, role: "tabpanel", id: selectedIndex !== undefined
                    ? `tabpanel-${tabInfoRef.current[selectedIndex].uniqueId}`
                    : undefined, tabIndex: 0, "aria-label": 
                // eslint-disable-next-line no-nested-ternary
                isAndroid && selectedIndex !== undefined
                    ? `${selectedLabel} ${tabInfoRef.current[selectedIndex].tabPanelAriaLabel || ''}`
                    : !isAndroid &&
                        selectedIndex !== undefined &&
                        !tabInfoRef.current[selectedIndex].tabPanelAriaLabel
                        ? selectedLabel
                        : undefined, children: _renderContent() }))] }));
};

export { Tabs, Tabs as default };
